import { useState, useCallback, useRef } from 'react';
import { createStreamUpload, proxyUploadVideo, initChunkedUpload, uploadChunk, completeChunkedUpload } from '@/lib/api';

// Cloudflare direct upload limit - larger files use server proxy
const DIRECT_UPLOAD_LIMIT = 200 * 1024 * 1024; // 200MB

// Detect if running on mobile device
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // Check for mobile user agents
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  // Also check for touch device and small screen
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth < 768;
  return isMobileUA || (hasTouch && isSmallScreen);
};

// Detect iOS specifically (more aggressive memory management needed)
const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /iPhone|iPad|iPod/i.test(userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad with iOS 13+
};

// Mobile chunked upload threshold - use chunked for files > 20MB on mobile
// This is lower than desktop because mobile browsers have stricter memory limits
const MOBILE_CHUNK_THRESHOLD = 20 * 1024 * 1024; // 20MB

// iOS needs even smaller threshold due to Safari memory limits
const IOS_CHUNK_THRESHOLD = 10 * 1024 * 1024; // 10MB - use chunked for smaller files on iOS

/**
 * Get the actual duration of a video file using HTML5 video element
 * Has a timeout for mobile browsers where metadata loading can fail silently
 */
const getVideoDuration = (file) => {
  return new Promise((resolve) => {
    // Timeout after 10 seconds - mobile browsers can fail silently
    const timeoutId = setTimeout(() => {
      console.warn('Video duration detection timed out, using 10 minute default');
      resolve(600);
    }, 10000);
    
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        try {
          window.URL.revokeObjectURL(video.src);
        } catch (e) {}
        const duration = Math.ceil(video.duration);
        resolve(duration);
      };
      
      video.onerror = () => {
        clearTimeout(timeoutId);
        try {
          window.URL.revokeObjectURL(video.src);
        } catch (e) {}
        console.warn('Could not detect video duration, using 10 minute default');
        resolve(600);
      };
      
      video.src = URL.createObjectURL(file);
    } catch (e) {
      clearTimeout(timeoutId);
      console.warn('Video duration detection error:', e);
      resolve(600);
    }
  });
};

/**
 * Custom hook for uploading videos to Cloudflare Stream.
 * - Files < 200MB: Direct browser upload to Cloudflare
 * - Files >= 200MB: Server-side proxy upload (handles TUS protocol)
 * - Mobile: Uses chunked upload for files > 20MB (optimized for iOS Safari)
 */
export function useCloudflareUpload() {
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(''); // Human-readable status for mobile users
  const xhrRef = useRef(null);
  const cancelSourceRef = useRef(null);
  const keepAliveRef = useRef(null); // Prevent iOS from suspending the upload

  // Start a keep-alive interval to prevent iOS from suspending the upload
  const startKeepAlive = useCallback(() => {
    if (keepAliveRef.current) clearInterval(keepAliveRef.current);
    keepAliveRef.current = setInterval(() => {
      // Touch a dummy operation to keep JS alive
      const _ = Date.now();
    }, 5000);
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, []);

  const startUpload = useCallback(async (file, onSuccess, onError) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadStatus('Preparing upload...');

    // Validate file
    if (!file) {
      const error = new Error('No file provided');
      setUploadError({ message: error.message });
      setIsUploading(false);
      setUploadStatus('');
      if (onError) onError({ message: error.message });
      throw error;
    }

    // On iOS, file might be a different type
    let fileSize, fileName;
    try {
      fileSize = file.size;
      fileName = file.name || 'video.mp4';
      
      if (!fileSize || fileSize === 0) {
        throw new Error('File appears to be empty');
      }
    } catch (e) {
      const error = new Error(`Cannot read file: ${e.message}`);
      setUploadError({ message: error.message });
      setIsUploading(false);
      setUploadStatus('');
      if (onError) onError({ message: error.message });
      throw error;
    }

    const isMobile = isMobileDevice();
    const isIOS = isIOSDevice();
    
    // On iOS, use chunked upload for files > 10MB (stricter due to Safari memory limits)
    // On other mobile, use chunked for files > 20MB
    // On desktop, use proxy for files > 200MB
    const chunkThreshold = isIOS ? IOS_CHUNK_THRESHOLD : MOBILE_CHUNK_THRESHOLD;
    const useChunked = isMobile && fileSize > chunkThreshold;
    const useProxy = !useChunked && fileSize > DIRECT_UPLOAD_LIMIT;
    
    const uploadMethod = useChunked ? (isIOS ? 'iOS chunked' : 'mobile chunked') : (useProxy ? 'server proxy' : 'direct upload');

    // Start keep-alive for mobile uploads
    if (isMobile) {
      startKeepAlive();
      setUploadStatus(`Starting ${uploadMethod}...`);
    }

    try {
      // Get actual video duration
      // Skip duration detection on mobile - it can hang with large files
      let actualDuration;
      if (isMobile) {
        // Estimate based on file size: ~10MB per minute for typical video
        actualDuration = Math.max(60, Math.ceil(fileSize / (10 * 1024 * 1024) * 60));
        setUploadStatus('Analyzing video...');
      } else {
        try {
          actualDuration = await getVideoDuration(file);
        } catch (durationError) {
          console.warn('Duration detection failed, using default:', durationError);
          actualDuration = 600; // 10 min default
        }
      }
      const durationWithBuffer = Math.ceil(actualDuration * 1.2); // 20% buffer

      let videoId;
      
      if (useChunked) {
        // Mobile - use chunked upload
        setUploadStatus('Starting chunked upload...');
        videoId = await uploadViaChunks(file, fileSize, fileName, durationWithBuffer, onSuccess, onError, setUploadStatus);
      } else if (useProxy) {
        // Desktop large file - use server proxy
        setUploadStatus('Starting server upload...');
        videoId = await uploadViaProxy(file, fileSize, fileName, durationWithBuffer, onSuccess, onError);
      } else {
        // Small file - try direct upload, fallback to chunked on mobile if it fails
        try {
          setUploadStatus('Uploading to cloud...');
          videoId = await uploadDirect(file, fileSize, fileName, durationWithBuffer, onSuccess, onError);
        } catch (directError) {
          // If direct upload fails on mobile, try chunked upload as fallback
          if (isMobile) {
            console.warn('Direct upload failed on mobile, trying chunked upload...', directError.message);
            setUploadStatus('Retrying with chunked upload...');
            videoId = await uploadViaChunks(file, fileSize, fileName, durationWithBuffer, onSuccess, onError, setUploadStatus);
          } else {
            throw directError;
          }
        }
      }

      stopKeepAlive();
      setUploadStatus('');
      return videoId;

    } catch (error) {
      console.error('Upload failed:', error);
      stopKeepAlive();
      const errorMessage = error.message || 'Upload failed';
      setUploadError({ message: errorMessage });
      setUploadStatus('');
      setIsUploading(false);
      if (onError) onError({ message: errorMessage });
      throw error;
    }
  }, [startKeepAlive, stopKeepAlive]);

  /**
   * Upload large files via server proxy (> 200MB)
   */
  const uploadViaProxy = async (file, fileSize, fileName, duration, onSuccess, onError) => {
    // Generate temp ID for progress tracking
    const tempId = `proxy-${Date.now()}`;
    
    setUploadProgress(prev => ({
      ...prev,
      [tempId]: { percentage: 0, bytesUploaded: 0, bytesTotal: fileSize, status: 'uploading' }
    }));

    try {
      
      const response = await proxyUploadVideo(file, duration, (progressEvent) => {
        if (progressEvent.lengthComputable || progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(prev => ({
            ...prev,
            [tempId]: { percentage, bytesUploaded: progressEvent.loaded, bytesTotal: progressEvent.total, status: 'uploading' }
          }));
        }
      });

      const videoId = response.data.video_id;

      // Update progress with real video ID
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        newProgress[videoId] = { percentage: 100, bytesUploaded: fileSize, bytesTotal: fileSize, status: 'processing' };
        return newProgress;
      });

      setIsUploading(false);
      if (onSuccess) onSuccess({ videoId, fileName });
      return videoId;

    } catch (err) {
      // Clean up temp progress
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        return newProgress;
      });
      throw new Error(err.response?.data?.detail || err.message || 'Server upload failed');
    }
  };

  /**
   * Upload via chunked API - optimized for mobile devices
   * Sends small chunks (2MB for iOS, 5MB for others) one at a time to avoid memory issues
   */
  const uploadViaChunks = async (file, fileSize, fileName, duration, onSuccess, onError, setStatus) => {
    const tempId = `chunked-${Date.now()}`;
    const isIOS = isIOSDevice();
    
    // iOS Safari has stricter memory limits, use 2MB chunks
    // Other mobile browsers can handle 5MB chunks
    const preferredChunkSize = isIOS ? 2 * 1024 * 1024 : 5 * 1024 * 1024;
    
    setUploadProgress(prev => ({
      ...prev,
      [tempId]: { percentage: 0, bytesUploaded: 0, bytesTotal: fileSize, status: 'initializing', statusText: 'Preparing upload...' }
    }));
    if (setStatus) setStatus('Initializing upload...');

    let upload_id = null;
    let chunk_size = preferredChunkSize;
    let total_chunks = Math.ceil(fileSize / chunk_size);

    try {
      // Initialize chunked upload
      
      try {
        // Use native fetch for better iOS compatibility
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        
        
        const initFetchResponse = await fetch(`${apiBase}/api/stream/upload/chunked/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify({
            file_name: fileName,
            file_size: fileSize,
            max_duration_seconds: duration,
            preferred_chunk_size: preferredChunkSize  // Request smaller chunks for iOS
          })
        });
        
        if (!initFetchResponse.ok) {
          const errorData = await initFetchResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || `Server error: ${initFetchResponse.status}`);
        }
        
        const initData = await initFetchResponse.json();
        upload_id = initData.upload_id;
        chunk_size = initData.chunk_size;
        total_chunks = initData.total_chunks;
      } catch (initErr) {
        console.error('Failed to initialize chunked upload:', initErr);
        // Provide more specific error messages
        let errorMsg = 'Upload initialization failed';
        if (initErr.message === 'Network Error' || initErr.name === 'TypeError') {
          errorMsg = 'Network connection issue. Please check your internet and try again.';
        } else if (initErr.message?.includes('timeout')) {
          errorMsg = 'Request timed out. Please try again.';
        } else if (initErr.message) {
          errorMsg = initErr.message;
        }
        throw new Error(errorMsg);
      }
      
      setUploadProgress(prev => ({
        ...prev,
        [tempId]: { ...prev[tempId], status: 'uploading', statusText: 'Uploading...' }
      }));
      if (setStatus) setStatus(`Uploading (0/${total_chunks} parts)...`);

      // Upload each chunk sequentially using native fetch for iOS compatibility
      let uploadedBytes = 0;
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      
      // Helper function to convert blob to a fresh blob (fixes iOS Safari cloning issue)
      const createSafeBlob = async (originalBlob) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            // Create a new Blob from the ArrayBuffer - this avoids iOS cloning issues
            const freshBlob = new Blob([reader.result], { type: originalBlob.type || 'application/octet-stream' });
            resolve(freshBlob);
          };
          reader.onerror = () => reject(new Error('Failed to read chunk data'));
          reader.readAsArrayBuffer(originalBlob);
        });
      };
      
      for (let i = 0; i < total_chunks; i++) {
        const start = i * chunk_size;
        const end = Math.min(start + chunk_size, fileSize);
        
        let chunkBlob;
        try {
          // Slice the file
          const rawChunk = file.slice(start, end);
          
          // Verify the chunk was created properly
          if (!rawChunk || rawChunk.size === 0) {
            throw new Error('Empty chunk');
          }
          
          // Convert to a fresh blob to fix iOS Safari "object could not be cloned" error
          // This is necessary because iOS Safari has issues with sliced Blob objects in FormData
          chunkBlob = await createSafeBlob(rawChunk);
          
        } catch (sliceErr) {
          console.error(`Failed to slice chunk ${i}:`, sliceErr);
          throw new Error(`Failed to read file chunk ${i + 1}: ${sliceErr.message}`);
        }
        
        
        // Use native fetch for chunk upload with improved retry logic
        const uploadChunkWithFetch = async (retryCount = 0) => {
          const formData = new FormData();
          formData.append('file', chunkBlob, `chunk_${i}`);
          
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout per chunk
            
            const response = await fetch(`${apiBase}/api/stream/upload/chunked/${upload_id}/chunk/${i}`, {
              method: 'POST',
              headers: {
                'Authorization': token ? `Bearer ${token}` : ''
              },
              body: formData,
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.detail || `Chunk upload failed: ${response.status}`);
            }
            
            return await response.json();
          } catch (err) {
            // Retry on network errors or timeouts
            if (retryCount < 3 && (err.name === 'AbortError' || err.name === 'TypeError' || err.message?.includes('network'))) {
              const waitTime = 1000 * Math.pow(2, retryCount); // Exponential backoff: 1s, 2s, 4s
              console.warn(`Chunk ${i} failed (${err.message}), retry ${retryCount + 1}/3 in ${waitTime}ms...`);
              if (setStatus) setStatus(`Retrying part ${i + 1}... (attempt ${retryCount + 2})`);
              await new Promise(r => setTimeout(r, waitTime));
              return uploadChunkWithFetch(retryCount + 1);
            }
            throw err;
          }
        };
        
        await uploadChunkWithFetch();
        uploadedBytes = end;
        
        const percentage = Math.round((uploadedBytes / fileSize) * 100);
        setUploadProgress(prev => ({
          ...prev,
          [tempId]: { 
            percentage, 
            bytesUploaded: uploadedBytes, 
            bytesTotal: fileSize, 
            status: 'uploading',
            statusText: `Uploading ${i + 1}/${total_chunks}...`
          }
        }));
        if (setStatus) setStatus(`Uploading (${i + 1}/${total_chunks} parts)... ${percentage}%`);
      }

      // Trigger background assembly + Cloudflare upload (returns immediately)
      setUploadProgress(prev => ({
        ...prev,
        [tempId]: { ...prev[tempId], percentage: 100, status: 'finalizing', statusText: 'Processing video...' }
      }));
      if (setStatus) setStatus('Processing video... Please wait.');

      // This now returns immediately - server processes in background
      const completeController = new AbortController();
      const completeTimeoutId = setTimeout(() => completeController.abort(), 30000); // 30s is enough since it returns immediately
      
      const completeResponse = await fetch(`${apiBase}/api/stream/upload/chunked/${upload_id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        signal: completeController.signal
      });
      
      clearTimeout(completeTimeoutId);
      
      if (!completeResponse.ok) {
        const errorData = await completeResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to finalize upload');
      }
      
      // Poll status endpoint until complete or failed
      if (setStatus) setStatus('Uploading to cloud... This may take a moment.');
      
      let videoId = null;
      let pollAttempts = 0;
      const maxPollAttempts = 300; // 10 minutes at 2s intervals
      
      while (pollAttempts < maxPollAttempts) {
        await new Promise(r => setTimeout(r, 2000)); // Poll every 2 seconds
        pollAttempts++;
        
        try {
          const statusResponse = await fetch(`${apiBase}/api/stream/upload/chunked/${upload_id}/status`, {
            headers: { 'Authorization': token ? `Bearer ${token}` : '' }
          });
          
          if (!statusResponse.ok) continue;
          
          const statusData = await statusResponse.json();
          
          if (statusData.status === 'complete' && statusData.video_id) {
            videoId = statusData.video_id;
            break;
          }
          
          if (statusData.status === 'failed') {
            throw new Error(statusData.error || 'Video processing failed on server');
          }
          
          // Update progress display
          const cfPct = statusData.cf_upload_percent || 0;
          if (statusData.status === 'assembling') {
            if (setStatus) setStatus('Assembling video file...');
          } else if (statusData.status === 'uploading_to_cloudflare') {
            if (setStatus) setStatus(`Uploading to cloud... ${cfPct}%`);
          }
          
          setUploadProgress(prev => ({
            ...prev,
            [tempId]: { ...prev[tempId], percentage: Math.min(99, 95 + Math.floor(cfPct * 0.05)), status: 'finalizing', statusText: `Cloud upload ${cfPct}%` }
          }));
          
        } catch (pollErr) {
          if (pollErr.message?.includes('failed on server')) throw pollErr;
          console.warn('Status poll error, retrying...', pollErr.message);
        }
      }
      
      if (!videoId) {
        throw new Error('Video processing timed out. The upload may still be processing - check back later.');
      }
      

      // Update progress with real video ID
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        newProgress[videoId] = { percentage: 100, bytesUploaded: fileSize, bytesTotal: fileSize, status: 'processing' };
        return newProgress;
      });

      setIsUploading(false);
      if (setStatus) setStatus('Upload complete!');
      if (onSuccess) onSuccess({ videoId, fileName });
      return videoId;

    } catch (err) {
      console.error('Chunked upload failed:', err);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        return newProgress;
      });
      throw new Error(err.response?.data?.detail || err.message || 'Mobile upload failed');
    }
  };

  /**
   * Upload small files directly to Cloudflare (< 200MB)
   */
  const uploadDirect = async (file, fileSize, fileName, duration, onSuccess, onError) => {
    // Get upload URL from backend
    let uploadUrl, videoId;
    
    try {
      const response = await createStreamUpload(duration);
      uploadUrl = response.data.upload_url;
      videoId = response.data.video_id;
      
      if (!uploadUrl || !videoId) {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Could not initialize upload');
    }

    // Initialize progress
    setUploadProgress(prev => ({
      ...prev,
      [videoId]: { percentage: 0, bytesUploaded: 0, bytesTotal: fileSize, status: 'uploading' }
    }));

    // Upload file
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(prev => ({
            ...prev,
            [videoId]: { percentage, bytesUploaded: event.loaded, bytesTotal: event.total, status: 'uploading' }
          }));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress(prev => ({
            ...prev,
            [videoId]: { ...prev[videoId], percentage: 100, status: 'processing' }
          }));
          setIsUploading(false);
          xhrRef.current = null;
          if (onSuccess) onSuccess({ videoId, fileName });
          resolve(videoId);
        } else {
          let errorMsg = `Upload failed (${xhr.status})`;
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.errors?.[0]?.message) {
              errorMsg = response.errors[0].message;
            }
          } catch (e) {}
          setIsUploading(false);
          xhrRef.current = null;
          reject(new Error(errorMsg));
        }
      };

      xhr.onerror = () => {
        setIsUploading(false);
        xhrRef.current = null;
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = () => {
        setIsUploading(false);
        xhrRef.current = null;
        reject(new Error('Upload cancelled'));
      };

      xhr.timeout = 30 * 60 * 1000; // 30 min
      xhr.ontimeout = () => {
        setIsUploading(false);
        xhrRef.current = null;
        reject(new Error('Upload timed out'));
      };

      const formData = new FormData();
      formData.append('file', file, fileName);
      
      xhr.open('POST', uploadUrl, true);
      xhr.send(formData);
    });
  };

  const cancelUpload = useCallback((videoId) => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[videoId];
      return newProgress;
    });
    setIsUploading(false);
  }, []);

  const clearError = useCallback(() => {
    setUploadError(null);
  }, []);

  return {
    uploadProgress,
    uploadError,
    isUploading,
    uploadStatus,
    startUpload,
    cancelUpload,
    clearError
  };
}

export default useCloudflareUpload;
